import type { PreviewSampleContext, PreviewSampleFieldVisibility } from '../types';

/**
 * 音声プレビューの「プレビュー条件」入力パネル (共通部品) — Issue #439。
 *
 * ## なぜ共通部品にするか
 *
 * プレビュー条件 (サンプル遅延 / 線名 / 乗換駅 / 方面) は
 *
 *   1. 個別フィールドのプレビュー (`AudioPreviewField`)
 *   2. コンテンツ全体・放送地点まるごとの通し試聴 (`BroadcastPointForm`)
 *
 * の **両方** から指定できる必要がある (「プレビューやコンテンツ全体をプレビューで、
 * プレビュー時の条件の入力ができるようにしてほしい」)。
 * 入力欄をそれぞれの画面で複製すると
 *
 *   - 一方だけ項目が増減して「画面によって指定できる条件が違う」
 *   - 遅延の分 ⇔ 秒 換算がズレて「入力した分数と鳴る音声が食い違う」
 *
 * といった不整合が起きるため、UI と換算規則を本部品に一元化する。
 *
 * ## 表示規則
 *
 * `fields` (凡例定義から `resolveSampleFields()` / `resolvePointSampleFields()` で
 * 導出) が true の項目だけを表示する。**本番で読み上げられない項目は入力させない**
 * (例: whole_line モードでは乗換駅・方面の欄を出さない)。
 */
export function PreviewSamplePanel({
  testIdSuffix,
  fields,
  sample,
  onChange,
}: {
  /**
   * `data-testid` のサフィックス。
   * 個別プレビューは `self-train-delay-0-1` のようなフィールド識別子、
   * 放送地点まるごとプレビューは `point` を渡す。
   */
  testIdSuffix: string;
  /** 表示する入力欄の種類 (凡例に載っている項目のみ true) */
  fields: PreviewSampleFieldVisibility;
  /** 現在のサンプル条件 (表示値) */
  sample: PreviewSampleContext;
  /** 1 項目の変更通知 (呼び出し側で「ユーザーが編集した」と記録する) */
  onChange: <K extends keyof PreviewSampleContext>(key: K, value: PreviewSampleContext[K]) => void;
}): React.JSX.Element {
  return (
    <div
      className="preview-sample-panel"
      data-testid={`preview-sample-panel-${testIdSuffix}`}
      style={{
        display: 'flex',
        gap: 8,
        flexWrap: 'wrap',
        alignItems: 'flex-end',
        margin: '6px 0',
        padding: '6px 8px',
        border: '1px solid #d0d7de',
        borderRadius: 4,
      }}
    >
      {fields.showDelay ? (
        <label style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span className="hint">遅延 (分)</span>
          <input
            type="number"
            min={0}
            step={1}
            style={{ width: 80 }}
            aria-label={`プレビュー条件 遅延分数 (${testIdSuffix})`}
            data-testid={`preview-sample-delay-${testIdSuffix}`}
            value={Math.floor((sample.delaySeconds ?? 0) / 60)}
            onChange={(e) => {
              const minutes = Math.max(0, Math.floor(Number(e.target.value) || 0));
              onChange('delaySeconds', minutes * 60);
            }}
          />
        </label>
      ) : null}
      {fields.showRouteName ? (
        <label style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span className="hint">線名</span>
          <input
            type="text"
            style={{ width: 120 }}
            aria-label={`プレビュー条件 線名 (${testIdSuffix})`}
            data-testid={`preview-sample-route-name-${testIdSuffix}`}
            value={sample.routeName ?? ''}
            onChange={(e) => onChange('routeName', e.target.value)}
          />
        </label>
      ) : null}
      {fields.showFromStation ? (
        <label style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span className="hint">乗換駅</span>
          <input
            type="text"
            style={{ width: 100 }}
            aria-label={`プレビュー条件 乗換駅 (${testIdSuffix})`}
            data-testid={`preview-sample-from-station-${testIdSuffix}`}
            value={sample.fromStation ?? ''}
            onChange={(e) => onChange('fromStation', e.target.value)}
          />
        </label>
      ) : null}
      {fields.showTransferStation ? (
        <label style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span className="hint">乗換駅</span>
          <input
            type="text"
            style={{ width: 100 }}
            aria-label={`プレビュー条件 乗換駅名 (${testIdSuffix})`}
            data-testid={`preview-sample-transfer-station-${testIdSuffix}`}
            value={sample.transferStation ?? ''}
            onChange={(e) => onChange('transferStation', e.target.value)}
          />
        </label>
      ) : null}
      {fields.showDirectionName ? (
        <label style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span className="hint">方面</span>
          <input
            type="text"
            style={{ width: 100 }}
            aria-label={`プレビュー条件 方面 (${testIdSuffix})`}
            data-testid={`preview-sample-direction-name-${testIdSuffix}`}
            value={sample.directionName ?? ''}
            onChange={(e) => onChange('directionName', e.target.value)}
          />
        </label>
      ) : null}
      <span className="hint" style={{ flexBasis: '100%' }}>
        試聴用のサンプル値です。実際の放送では運行情報から取得した値が使われます。
      </span>
    </div>
  );
}
